import { PRE_AUTH_PATH_KEY } from "@@common";
import { ConfigContext } from "@@components";
import { Configuration, EventMessage, EventType, PublicClientApplication } from "@azure/msal-browser";
import { MsalProvider } from "@azure/msal-react";
import { PropsWithChildren, useContext, useEffect, useMemo, useState } from "react";
import { IAuthService } from "../authentication/AuthService";
import Spinner from "../common/components/Spinner";
import AuthContext, { Auth } from "./AuthContext";
import { Config } from "./ConfigContext";

class AuthService implements IAuthService {
  private isAuthenticationInProgress = false;
  private isInitialized = false;

  constructor(
    private msalInstance: PublicClientApplication,
    private config: Config
  ) {
    msalInstance.addEventCallback((event) => {
      if (event.eventType === EventType.LOGIN_START || event.eventType === EventType.ACQUIRE_TOKEN_START) {
        this.isAuthenticationInProgress = true;
      }

      if (
        event.eventType === EventType.LOGIN_SUCCESS ||
        event.eventType === EventType.ACQUIRE_TOKEN_SUCCESS ||
        event.eventType === EventType.HANDLE_REDIRECT_END ||
        event.eventType === EventType.LOGIN_FAILURE ||
        event.eventType === EventType.ACQUIRE_TOKEN_FAILURE
      ) {
        this.isAuthenticationInProgress = false;
        this.ensureAccessToken(event);
      }
    });
  }

  setInitialized() {
    this.isInitialized = true;
  }

  async signIn(preservePath?: string): Promise<void> {
    // Check if an interaction is already in progress
    if (this.isAuthenticationInProgress) {
      console.warn("Authentication interaction already in progress");
      return;
    }

    // Store the current path for restoration after sign-in
    const pathToPreserve = preservePath || window.location.pathname + window.location.search;
    sessionStorage.setItem(PRE_AUTH_PATH_KEY, pathToPreserve);

    try {
      this.isAuthenticationInProgress = true;
      await this.msalInstance.loginRedirect({
        scopes: this.config.AUTH_SCOPE.split(" "),
        state: JSON.stringify({ returnPath: pathToPreserve }),
      });
    } catch (error) {
      this.isAuthenticationInProgress = false;
      console.error("Error during sign-in:", error);
      // If the error is interaction_in_progress, we can safely ignore it
      if (error instanceof Error && error.message.includes("interaction_in_progress")) {
        return;
      }
      throw error;
    }
  }

  async signOut(): Promise<void> {
    await this.msalInstance.logoutRedirect();
  }

  private async ensureAccessToken(event?: EventMessage): Promise<string | undefined> {
    if (!this.isInitialized) {
      console.warn("MSAL not yet initialized, skipping token acquisition");
      return undefined;
    }

    const accounts = this.msalInstance.getAllAccounts();
    if (accounts.length > 0) {
      const account = accounts[0];
      try {
        const r = await this.msalInstance.acquireTokenSilent({
          scopes: this.config.AUTH_SCOPE.split(" "),
          account: account,
        });
        return r.accessToken;
      } catch (error) {
        console.error("Error acquiring token silently:", error);
      }
    }
    return undefined;
  }

  getAccessToken(forceSignIn?: boolean): Promise<string | undefined> {
    return this.ensureAccessToken();
  }
}

const AuthContextProvider: React.FC<PropsWithChildren> = ({ children }) => {
  const config = useContext(ConfigContext);

  const { msalInstance, authService } = useMemo(() => {
    const msalConfig: Configuration = {
      auth: {
        clientId: config.AUTH_CLIENT_ID,
        authority: config.AUTH_AUTHORITY,
        knownAuthorities: config.AUTH_AUTHORITY_KNOWNS ? config.AUTH_AUTHORITY_KNOWNS.split(" ") : undefined,
        redirectUri: window.location.origin,
      },
      cache: {
        cacheLocation: "localStorage",
        storeAuthStateInCookie: false,
      },
    };

    const msalInstance = new PublicClientApplication(msalConfig);
    const authService = new AuthService(msalInstance, config);
    return { msalInstance, authService };
  }, [config]);

  const [accounts, setAccounts] = useState<any[]>([]);
  const [isInitializing, setIsInitializing] = useState(true);

  // Create stable auth object using useMemo to prevent unnecessary re-renders
  const auth = useMemo<Auth>(
    () => ({
      isAuthenticated: accounts.length > 0,
      authService,
      account: accounts.length > 0 ? accounts[0] : null,
    }),
    [accounts, authService]
  );

  useEffect(() => {
    const load = () => {
      const newAccounts = msalInstance.getAllAccounts();
      // Only update state if account actually changed (assuming single account)
      setAccounts((prevAccounts) => {
        const prevUsername = prevAccounts[0]?.username;
        const newUsername = newAccounts[0]?.username;

        // If both empty or same username, no change
        if (prevUsername === newUsername) {
          return prevAccounts;
        }

        console.log("AuthContextProvider - Auth state updated:", {
          isAuthenticated: newAccounts.length > 0,
          username: newUsername,
        });
        return newAccounts;
      });
    };

    // Initialize MSAL and handle redirect response
    const initializeMsal = async () => {
      try {
        console.log("MSAL: Initializing MSAL instance...");
        // Initialize the MSAL instance before any API calls
        await msalInstance.initialize();
        authService.setInitialized();
        console.log("MSAL: Instance initialized, starting handleRedirectPromise...");
        // This is critical - it processes the redirect response from Azure AD B2C
        const response = await msalInstance.handleRedirectPromise();
        if (response) {
          console.log("MSAL: Authentication redirect handled successfully", {
            account: response.account?.username,
            scopes: response.scopes,
            tokenType: response.tokenType,
          });
        } else {
          console.log("MSAL: No redirect response (normal page load)");
        }
        // Load auth state after handling redirect
        load();
      } catch (error) {
        console.error("MSAL: Error handling redirect promise:", error);
        // Still load the auth state even if there's an error
        load();
      } finally {
        setIsInitializing(false);
      }
    };

    initializeMsal();

    // Add event callback to handle auth state changes
    const callbackId = msalInstance.addEventCallback((event) => {
      console.log("MSAL Event:", event.eventType);
      if (
        event.eventType === EventType.LOGIN_SUCCESS ||
        event.eventType === EventType.ACQUIRE_TOKEN_SUCCESS ||
        event.eventType === EventType.SSO_SILENT_SUCCESS ||
        event.eventType === EventType.HANDLE_REDIRECT_END
      ) {
        console.log("MSAL: Reloading auth state due to event:", event.eventType);
        load();
      }
    });

    // Listen to storage events to sync auth state across browser tabs
    const handleStorageChange = (e: StorageEvent) => {
      // Check if MSAL-related keys have changed in localStorage
      if (e.key && e.key.startsWith("msal.")) {
        console.log("MSAL: Auth state changed in another tab, reloading...");
        load();
      }
    };
    window.addEventListener("storage", handleStorageChange);

    // Use BroadcastChannel for better cross-tab communication (if supported)
    let authChannel: BroadcastChannel | null = null;
    if (typeof BroadcastChannel !== "undefined") {
      try {
        authChannel = new BroadcastChannel("msal-auth-channel");
        authChannel.onmessage = (event) => {
          if (event.data === "auth-state-changed") {
            console.log("MSAL: Auth state changed notification via BroadcastChannel");
            load();
          }
        };
        // Notify other tabs when auth state changes in this tab
        const notifyOtherTabs = () => {
          authChannel?.postMessage("auth-state-changed");
        };
        // Broadcast on login/logout events
        msalInstance.addEventCallback((event) => {
          if (event.eventType === EventType.LOGIN_SUCCESS || event.eventType === EventType.LOGOUT_SUCCESS) {
            notifyOtherTabs();
          }
        });
      } catch (error) {
        console.warn("BroadcastChannel not available:", error);
      }
    }

    // Cleanup: remove event listeners and callbacks on unmount
    return () => {
      if (callbackId) {
        msalInstance.removeEventCallback(callbackId);
      }
      window.removeEventListener("storage", handleStorageChange);
      if (authChannel) {
        authChannel.close();
      }
    };
  }, [msalInstance, authService]);

  return (
    <MsalProvider instance={msalInstance}>{isInitializing ? <Spinner /> : <AuthContext.Provider value={auth}>{children}</AuthContext.Provider>}</MsalProvider>
  );
};

export default AuthContextProvider;
